// src/components/orders/OrdersTable.jsx
import React from "react";
import { Link } from "react-router-dom";
import { toApiDateTime, toDisplayDate } from "@lib/datetime";

const orderIdOf = (o) => String(o?.order_id ?? o?.id ?? "");

// Pickup / drop-off cell: "15 Oct 2026 · <time slot>", "-" when neither is set (WN-0224).
export const pickupLabel = (o) =>
  [toDisplayDate(o?.pickup_date), o?.pickup_time_slot ?? o?.pickup_slot].filter(Boolean).join(" · ") || "-";
export const dropoffLabel = (o) =>
  [toDisplayDate(o?.dropoff_date), o?.dropoff_time_slot ?? o?.dropoff_slot].filter(Boolean).join(" · ") || "-";
export const storeNameOf = (o) => String(o?.store?.name ?? o?.store_name ?? "").trim() || "-";

export default function OrdersTable({
  rows,
  selected,
  isAllOnPageSelected,
  isSomeOnPageSelected,
  onToggleOne,
  onToggleAllOnPage,
  jofotaraStatusById,
  processingOrderId,
}) {
  const headerRef = React.useRef(null);

  React.useEffect(() => {
    if (headerRef.current) {
      headerRef.current.indeterminate = Boolean(isSomeOnPageSelected);
    }
  }, [isSomeOnPageSelected]);

  return (
    <div className="oc-table-wrap">
      <table className="oc-table">
        <thead>
          <tr>
            <th style={{ width: 36 }}>
              <input
                ref={headerRef}
                type="checkbox"
                checked={Boolean(isAllOnPageSelected)}
                onChange={(e) => onToggleAllOnPage?.(e.target.checked)}
              />
            </th>
            <th>Order ID</th>
            <th>Customer</th>
            <th>Store Name</th>
            <th>Status</th>
            <th>Total</th>
            <th>Date Added</th>
            <th>Type</th>
            <th>Pickup</th>
            <th>Drop-off</th>
            <th style={{ width: 70 }}>JO-Fa</th>
            <th style={{ width: 120 }}>Action</th>
            <th>Created By</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((o) => {
            const oid = orderIdOf(o);
            const cid = o.customer?.customer_id ?? o.customer_id;
            const _lbl = (o.invoice_label || "").trim(); // per-order invoice label (WN-0113)
            // Legacy nameless customers → fall back to the phone so it's never blank.
            const _rawName = String(o.customer?.name ?? o.name ?? "").trim();
            const _phone = o.customer?.telephone || o.telephone || "";
            const cname = (_rawName || (_phone ? `No name · ${_phone}` : "No name")) + (_lbl ? ` (${_lbl})` : "");
            const status = o.status_name ?? o.status;
            // "Created By": customer self-service orders are all stored as "app";
            // show the real channel from platform (Website / App iOS / App Android).
            const _cb = String(o.created_by || "").trim();
            const _pf = String(o.platform || "").toLowerCase();
            const createdByLabel =
              _cb && _cb.toLowerCase() !== "app"
                ? _cb
                : _pf === "web" || _pf === "website"
                ? "Website"
                : _pf === "ios"
                ? "App (iOS)"
                : _pf === "android"
                ? "App (Android)"
                : _cb || "—";

            const jf = jofotaraStatusById?.[oid];
            const jfState = jf?.state || "idle";
            const jfMsg = jf?.message || "";

            const isProcRow = processingOrderId && String(processingOrderId) === String(oid);
            const rowStyle = o.complemantary_reason ? { backgroundColor: "#efd9d9" } : {};

            return (
              <tr key={oid} style={rowStyle}>
                <td>
                  <input
                    type="checkbox"
                    checked={selected?.has?.(oid) || false}
                    onChange={() => onToggleOne?.(oid)}
                  />
                </td>

                <td className="oc-mono">{oid}</td>

                <td>
                  {cid ? (
                    <Link to={`/customers/${cid}`}>{cname}</Link>
                  ) : (
                    cname
                  )}
                </td>

                <td>{storeNameOf(o)}</td>

                <td>
                  {status === "Pick up" && <span className="oc-badge blue">Pick up</span>}
                  {status === "Processing" && <span className="oc-badge amber">Processing</span>}
                  {status === "Cleaning" && <span className="oc-badge orange">Cleaning</span>}
                  {status === "Delivery" && <span className="oc-badge red">Delivery</span>}
                  {status === "Complete" && <span className="oc-badge green">Complete</span>}
                  {status === "Canceled" && <span className="oc-badge red">Canceled</span>}
                  {status === "Chargeback" && <span className="oc-badge pink">Chargeback</span>}
                  {status === "Skip Selection" && <span className="oc-badge orange">Skip Selection</span>}
                  {status === "Reschedule" && <span className="oc-badge amber">Reschedule</span>}
                  {status === "Pending for customer support" && <span className="oc-badge red">Pending for customer support</span>}
                  {!["Pick up", "Processing", "Cleaning", "Delivery", "Complete", "Canceled", "Chargeback", "Skip Selection", "Reschedule", "Pending for customer support"].includes(status) && (
                    <span className="oc-badge gray">{status}</span>
                  )}
                </td>

                <td className="oc-mono">{isNaN(Number(o.total)) ? o.total : Number(o.total || 0).toFixed(2)}</td>
                <td className="oc-mono">{toApiDateTime(o.date ?? o.date_added)}</td>
                <td>{o.type ?? o.order_type}</td>
                <td className="oc-mono">{pickupLabel(o)}</td>
                <td className="oc-mono">{dropoffLabel(o)}</td>

                <td>
                  {isProcRow && <span className="oc-badge blue" title="Sending…">…</span>}

                  {!isProcRow && jfState === "pass" && (
                    <span className="oc-badge green" title={jfMsg || "Sent to JoFotara"}>
                      ✓
                    </span>
                  )}

                  {!isProcRow && jfState === "processing" && (
                    <span className="oc-badge blue" title={jfMsg || "Sending…"}>
                      …
                    </span>
                  )}

                  {!isProcRow && jfState === "fail" && (
                    <span className="oc-badge red" title={jfMsg || "Failed"}>
                      ✕
                    </span>
                  )}

                  {!isProcRow && jfState === "idle" && (
                    <span className="oc-badge gray">—</span>
                  )}
                </td>

                <td>
                  <div className="oc-row-actions">
                    <Link to={`/orders/${o.order_id ?? o.id ?? oid}`}>
                      <button className="oc-ico-btn" title="View">👁️</button>
                    </Link>
                  </div>
                </td>

                <td>{createdByLabel}</td>
              </tr>
            );
          })}

          {rows.length === 0 && (
            <tr>
              <td colSpan={13} style={{ textAlign: "center", padding: 24 }}>
                No orders found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

